import { Component, computed, inject, Input, OnInit, signal } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { ToastrService } from "ngx-toastr";

import { Constants } from "../../../constants/constants";
import { PageMode } from "../../../constants/enums";
import { BenchmarkEntryInfoType, CardEntryInfoType, ValidatorModelType } from "../../../models";
import { FormDirtyCheckerService, ValidatorApiService } from "../../../services";
import { AttachmentsListComponent } from "../../attachments";
import { FormInputComponent } from "../../form";

@Component({
  selector: "adaa-edit-data-entry-modal",
  imports: [TranslatePipe, ReactiveFormsModule, FormInputComponent, AttachmentsListComponent],
  templateUrl: "./edit-data-entry-modal.html",
  styleUrl: "./edit-data-entry-modal.scss",
})
export class EditDataEntryModalComponent implements OnInit {
  private readonly _formBuilder = inject(FormBuilder);
  private readonly _toastrService = inject(ToastrService);
  private readonly _translateService = inject(TranslateService);
  private readonly _formDirtyCheckerService = inject(FormDirtyCheckerService);
  private readonly _validatorApiService = inject(ValidatorApiService);
  readonly modal = inject(NgbActiveModal);

  @Input() header: string;
  @Input() dataEntry: CardEntryInfoType | BenchmarkEntryInfoType;
  @Input() formValidation: ValidatorModelType;
  @Input() pageMode: PageMode;
  @Input() showSubmit: boolean = true;

  //Benchmarks only hold notes and attachments, they have no analysis fields and are keyed by year
  readonly showNotesOnly = signal<boolean>(false);

  readonly notesColumnClass = computed(() => (this.showNotesOnly() ? "col-12" : "col-sm-6 col-xs-12"));

  submitted: boolean = false;
  Constants = Constants;
  dataEntryForm: FormGroup;

  public ngOnInit(): void {
    this._prepareForm();
    this.dataEntryForm.patchValue(this.dataEntry);
  }

  public submit() {
    this.submitted = true;
    if (this.dataEntryForm.invalid) return;

    const data = this.dataEntryForm.getRawValue();

    if (!this._doesFormHaveAnyChanges(data)) {
      this._toastrService.warning(this._translateService.instant("notification.warning.no_data_to_modify"));
      return;
    }

    this.modal.close(data);
  }

  private _prepareForm() {
    const isViewMode = this.pageMode === PageMode.view;

    if (this.showNotesOnly()) {
      this._prepareNotesOnlyForm(isViewMode);
      return;
    }

    this.dataEntryForm = this._formBuilder.group({
      itemId: { value: undefined, disabled: isViewMode },
      itemType: { value: undefined, disabled: isViewMode },
      periodId: { value: undefined, disabled: isViewMode },
      analysisAE: { value: undefined, disabled: isViewMode },
      analysisEN: { value: undefined, disabled: isViewMode },
      recommendationsAE: [
        { value: undefined, disabled: isViewMode },
        [Validators.minLength(3), Validators.maxLength(Constants.FORM_FIELD_CONSTRAINTS.DESC_MAXLENGTH)],
      ],
      recommendationsEN: { value: undefined, disabled: isViewMode },
      areasOfImprovementsAE: [
        { value: undefined, disabled: isViewMode },
        [Validators.minLength(3), Validators.maxLength(Constants.FORM_FIELD_CONSTRAINTS.DESC_MAXLENGTH)],
      ],
      areasOfImprovementsEN: { value: undefined, disabled: isViewMode },
      entryFile: { value: undefined, disabled: isViewMode },
      filename: { value: undefined, disabled: isViewMode },
      attachType: { value: undefined, disabled: isViewMode },
      pmoNotesEN: { value: undefined, disabled: isViewMode },
      pmoNotesAE: { value: undefined, disabled: isViewMode },
      attachFileList: { value: undefined, disabled: isViewMode },
      id: { value: undefined, disabled: isViewMode },
      status: { value: undefined, disabled: isViewMode },
      updateTS: { value: undefined, disabled: isViewMode },
      updateUser: { value: undefined, disabled: isViewMode },
    });

    this.dataEntryForm = this._validatorApiService.setValidationFromData(this.formValidation, this.dataEntryForm);
  }

  private _prepareNotesOnlyForm(isViewMode: boolean) {
    this.dataEntryForm = this._formBuilder.group({
      itemId: { value: undefined, disabled: isViewMode },
      itemType: { value: undefined, disabled: isViewMode },
      year: { value: undefined, disabled: isViewMode },
      pmoNotesEN: { value: undefined, disabled: isViewMode },
      pmoNotesAE: [
        { value: undefined, disabled: isViewMode },
        [Validators.minLength(3), Validators.maxLength(Constants.FORM_FIELD_CONSTRAINTS.DESC_MAXLENGTH)],
      ],
      attachFileList: { value: [], disabled: isViewMode },
      id: { value: undefined, disabled: isViewMode },
      status: { value: undefined, disabled: isViewMode },
      updateTS: { value: undefined, disabled: isViewMode },
      updateUser: { value: undefined, disabled: isViewMode },
    });

    this.dataEntryForm = this._validatorApiService.setValidationFromData(this.formValidation, this.dataEntryForm);
  }

  private _doesFormHaveAnyChanges(model: CardEntryInfoType | BenchmarkEntryInfoType): boolean {
    return this._formDirtyCheckerService.isDirty(model, this.dataEntry, {
      fieldsToCheckStatus: ["attachFileList"],
      fieldsToIgnore: [
        "itemId",
        "itemType",
        "periodId",
        "year",
        "analysisEN",
        "recommendationsEN",
        "areasOfImprovementsEN",
        "entryFile",
        "filename",
        "attachType",
        "pmoNotesEN",
        "id",
        "status",
        "updateTS",
        "updateUser",
      ],
    });
  }
}
